import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useStoreState } from '@/stores/store';
import { prepareJson } from '../utils/prepareJson';
import cloneDeep from 'lodash/cloneDeep';
import { useNavigation } from '@react-navigation/native';
import { hasOwnProperty } from '@/utils/utils';

import store from '../stores/store';
import { isPatientApp } from '@/config/constants';
import starUtil from '@/utils/starUtil';
import { useDialog } from './useDialog';
import { useAudioContext } from '@/contexts/AudioContext/useAudioContext';
import { logError } from '@/utils/logHandling';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import {
  ExerciseClass,
  ExerciseSettings,
  Modality,
  Patient,
} from '@/stores/store.types';

interface WordExerciseItem {
  correct: string[];
  exerciseClass: ExerciseClass;
  partsOfSpeech: string;
  retinaImage: string;
  type: Modality;
  video: string;
  word: string;
  wordsetitem: string;
  related?: string[];
  unrelated?: string[];
}

interface SentenceExerciseItem {
  audio: string;
  correct: string[];
  exerciseClass: ExerciseClass;
  optionalWrongWords: string[];
  partsOfSpeech: string;
  retinaImage: string;
  sentence: string;
  type: Modality;
  word: string;
  wordsetitem: string;
  related?: string[];
  unrelated?: string[];
}

interface TextExerciseItem {
  exerciseClass: ExerciseClass;
  questions: {
    choices: {
      choiceAudio: string;
      isCorrect: boolean;
    };
    questionType: string;
    questionAudio: string;
  }[];
  type: Modality;
  wordsetitem: string;

  textNormalAudioURL?: string;
  textSlowAudioURL?: string;

  textContent?: string;
  title?: string;
  textTitle?: string;
}

const { touched, stop, uploadResults, getTodaysProgress, upload, applyUserId } =
  starUtil;

const DEFAULT_STATE = {
  nextButton: 'Überspringen',
  exerciseid: 0,
  currentExercise: 0,
  lastExercise: 0,
  exercises: [],
  exerciseClass: '',
  settings: null,
  zielwort: '',
  audioButtonText: '',
  ablenkerFromSetJson: '',
  // ? Patient Specific code (when used in EigenTraining)
  isSliding: false,
  currentProgress: 0,
};

let timeoutReference: ReturnType<typeof setTimeout> | null = null;

const reducer = (state = DEFAULT_STATE, action) => {
  const { type, payload } = action;
  switch (type) {
    case 'CHANGE': {
      return { ...state, ...payload };
    }
    default: {
      return state;
    }
  }
};

function generateSettings(
  activePatient: Patient | null,
  exerciseClass: 'word' | 'sentence' | 'text',
  activatedExerciseModalities: {
    Lesen: boolean;
    Schreiben: boolean;
    Sprechen: boolean;
    Verstehen: boolean;
  },
) {
  const settings: any = {};

  const patient = activePatient ?? store.getState().activePatient;

  if (!patient) {
    logError('No patient found in generateSettings');
    return {
      dailyPracticeDurationGoal: 60,
    };
  }
  const exerciseTypesKey = `${exerciseClass}setExerciseTypes` as const;
  const exerciseTypes = patient?.[exerciseTypesKey] || [];

  settings.Verstehen = {
    setting: exerciseTypes[0]?.setting,
    value: exerciseTypes[0]?.value,
    active: activatedExerciseModalities.Verstehen,
    playbackRate: exerciseTypes[0]?.playbackRate,
  };

  settings.Sprechen = {
    setting: exerciseTypes[1]?.setting,
    active: activatedExerciseModalities.Sprechen,
    playbackRate: exerciseTypes[1]?.playbackRate,
    value: exerciseTypes[1]?.value,
  };

  settings.Schreiben = {
    setting: exerciseTypes[2]?.setting,
    value: exerciseTypes[2]?.value,
    active: activatedExerciseModalities.Schreiben,
    playbackRate: exerciseTypes[2]?.playbackRate,
  };

  settings.Lesen = {
    setting: exerciseTypes[3]?.setting,
    value: exerciseTypes[3]?.value,
    active: activatedExerciseModalities.Lesen,
    playbackRate: exerciseTypes[3]?.playbackRate,
  };

  settings.dailyPracticeDurationGoal = hasOwnProperty(
    patient,
    'dailyPracticeDurationGoal',
  )
    ? patient?.dailyPracticeDurationGoal
    : 60;

  if (patient && hasOwnProperty(patient, 'activateDailyGoal')) {
    settings.activateDailyGoal = patient?.activateDailyGoal;
  }

  if (patient && hasOwnProperty(patient, 'audioSpeedSetting')) {
    settings.audioSpeedSetting = patient?.audioSpeedSetting;
  }

  return settings;
}

// Method to decide how Zielword button is named
function getZielwortName({
  currentExercise,
  exercises,
}: {
  currentExercise: number;
  exercises: (WordExerciseItem | SentenceExerciseItem | TextExerciseItem)[];
}) {
  if (exercises[currentExercise]?.exerciseClass === 'sentence') {
    return 'Zielsatz';
  } else {
    return 'Zielwort';
  }
}

interface AufgabenExerciseProps {
  setId: string;
  isTextSet: boolean;
  patientId: string;
  localWordsetProp: {
    ablenkerFromSetJson: string;
    exerciseClass: 'sentence' | 'word' | 'text';
    patientId?: string;
    activeWordset:
      | {
          sentenceset: any;
        }
      | {
          wordset: any;
        }
      | {
          textset: any;
        };
    assets: any[];
    cardSettings?: { setting: string; images: number };
    exerciseSettings: {
      Lesen: ExerciseSettings;
      Schreiben: ExerciseSettings;
      Sprechen: ExerciseSettings;
      Verstehen: ExerciseSettings;
      audioSpeedSetting?: string;
    };
    preparedSet?: {
      date: string;
      exercises: (WordExerciseItem | SentenceExerciseItem | TextExerciseItem)[];
    };
    sync: boolean;
    videosAdded?: boolean;
  };
  goHome: () => void;
  isSelfTraining: boolean;
  activatedExerciseModalities: {
    Lesen: boolean;
    Schreiben: boolean;
    Sprechen: boolean;
    Verstehen: boolean;
  };
}

export const useAufgabenExercise = ({
  setId,
  isTextSet,
  patientId,
  localWordsetProp,
  goHome,
  isSelfTraining,
  activatedExerciseModalities,
}: AufgabenExerciseProps) => {
  const { showDialog, removeDialog } = useDialog();
  const { stopSound } = useAudioContext();
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const localWordset = useMemo(() => cloneDeep(localWordsetProp), []);
  const { goBack } = useNavigation();
  const PAGER_OPTIONS = state.exercises.map((exercise) => exercise.id);

  const patient = useStoreState((store) => {
    return store.downloadPatients.find((patient) => patient._id == patientId);
  });

  const activeExercise = state.exercises[state.currentExercise];
  const maxNumOfExercises = useMemo(() => {
    if (!isTextSet) {
      return 0;
    }
    const counter = state.exercises?.reduce((count, obj) => {
      if (obj.type === 'Verstehen') {
        count++;
      }
      return count;
    }, 0);
    return counter;
  }, [state.exercises?.length, isTextSet]);

  // functions
  const stateDispatcher = useCallback((payload: Partial<typeof state>) => {
    dispatch({
      type: 'CHANGE',
      payload,
    });
  }, []);

  const showResultsModalChange = useCallback((value: boolean) => {
    setShowResultsModal(value);
  }, []);

  const skip = () => {
    nextSlide(0, state.currentExercise, true);
  };

  const next = () => {
    nextSlide(0, state.currentExercise, false);
  };

  const isSolved = useCallback(() => {
    stateDispatcher({ nextButton: 'Weiter' });
  }, []);

  const notSolved = useCallback(() => {
    stateDispatcher({ nextButton: 'Überspringen' });
  }, []);

  //Method to slide to next exercise and log data
  const nextSlide = (sleep, exerciseid, skipped) => {
    if (isSelfTraining) {
      notSolved();
      if (!state.isSliding) {
        stateDispatcher({ isSliding: true });
        setTimeout(function () {
          checkProgress(skipped, true);
        }, sleep);
      }
    } else {
      if (typeof exerciseid === 'undefined') {
        exerciseid = state.currentExercise;
      }
      //add skipped result if exercise is skipped
      setTimeout(function () {
        const CONDITION = state.currentExercise + 1 === state.exercises.length;
        if (CONDITION) {
          const { exerciseClass } = activeExercise;
          if (exerciseClass === 'text') {
            goBack();
          } else {
            setShowResultsModal(true);
          }
          return;
        }
        // slide to next slide
        const newNextButtonValue = 'Überspringen';
        const newCurrentExerciseValue = parseInt(exerciseid) + 1;
        stateDispatcher({
          currentExercise: newCurrentExerciseValue,
          nextButton: newNextButtonValue,
        });
      }, sleep);
    }
  };

  const checkProgress = (skipped, goToNextSlide, isText) => {
    if (!skipped && state.settings.activateDailyGoal) {
      getTodaysProgress().then((data) => {
        if (!data) {
          logError(
            'Failed to get progress for excercise (checkProgress), no data received',
          );
          return;
        }

        if (data.showPopup === true) {
          if (isText) {
            stopSound();
          }
          stop().then(() => {
            stateDispatcher({
              currentProgress: 1,
            });
            const minutesExercised = data.stars + '0';
            showDialog(
              PREPARED_DIALOGS.starsProgressDialog({
                minutesExercised,
                stars: data.stars,
                patient,
                onConfirm: () => {
                  removeDialog();
                  onWeiterPress(isText ? false : goToNextSlide);
                },
                onStop: () => {
                  onHomePress();
                  removeDialog();
                },
              }),
            );
          });
        } else {
          stateDispatcher({
            currentProgress: data.progress * 0.95,
          });
          if (goToNextSlide && !isText) {
            nextSlideLogic();
          }
        }
      });
    } else {
      if (isText && !state.settings.activateDailyGoal) {
        return;
      }
      if (goToNextSlide) {
        nextSlideLogic();
      }
    }
  };

  // switch to next exercise
  const nextSlideLogic = () => {
    stateDispatcher({ isSliding: false });
    if (state.currentExercise + 1 === state.exercises.length) {
      // if star popup is off go back to menu
      if (!state.settings.activateDailyGoal) {
        stop().then(() => {
          uploadResults().then(() => {
            upload().then(() => {
              goHome();
            });
          });
        });
      }
      // else generate exercises again and repeat
      else {
        setTimeout(() => {
          init();
        });
      }
    } else {
      // slide to next slide
      stateDispatcher({
        currentExercise: state.currentExercise + 1,
      });
    }
  };

  const goSelect = () => {
    stop()
      .then(() => {
        getTodaysProgress().then((data) => {
          if (!data) {
            logError(
              'Failed to get progress for excercise (goSelect), no data received',
            );
            onGoSelect();
            return;
          }

          if (state.settings.activateDailyGoal && data.showPopup === true) {
            stateDispatcher({
              currentProgress: 1,
            });
            const minutesExercised = data.stars + '0';
            showDialog(
              PREPARED_DIALOGS.starsProgressDialog({
                minutesExercised,
                stars: data.stars,
                patient,
                onConfirm: () => {
                  removeDialog();
                  onWeiterPress(false);
                },
                onStop: () => {
                  onHomePress();
                  removeDialog();
                },
              }),
            );
          } else {
            onGoSelect();
          }
        });
      })
      .catch((error) => {
        logError('', error);
      });
  };

  const showZielwort = () => {
    let timeoutTime = 1000;
    let revealedName = '';
    if (state.exercises[state.currentExercise].exerciseClass === 'sentence') {
      timeoutTime = 2500;
      revealedName = state.exercises[state.currentExercise].sentence;
    } else {
      revealedName = state.exercises[state.currentExercise].word;
    }
    stateDispatcher({ zielwort: revealedName });
    const zielwortName = getZielwortName({
      exercises: state.exercises,
      currentExercise: state.currentExercise,
    });
    timeoutReference = setTimeout(function () {
      stateDispatcher({ zielwort: zielwortName });
    }, timeoutTime);
  };

  const init = () => {
    let settings = null;

    if (isSelfTraining) {
      settings = generateSettings(
        isPatientApp ? null : (patient ?? null),
        localWordset.exerciseClass,
        activatedExerciseModalities,
      );
      // Persist self training selection in the store
      store
        .getActions()
        .changeSelfTrainingSelection(activatedExerciseModalities);
    } else {
      settings = localWordset.exerciseSettings;
    }
    const exercises = prepareJson(
      setId,
      localWordset,
      isSelfTraining ? settings : undefined,
    );

    let exerciseClass, zielwort, audioButtonText, ablenkerFromSetJson;
    // set exercise class to differ between text and words/sentences
    if (localWordset.exerciseClass) {
      exerciseClass = localWordset.exerciseClass;
    } else {
      exerciseClass = 'word';
    }

    ablenkerFromSetJson = localWordset.ablenkerFromSetJson;

    if (!isSelfTraining) {
      // add content of therapist toolbar and additional Ablenker from set for word/sentence exercises only
      if (exerciseClass === 'word' || exerciseClass === 'sentence') {
        zielwort = getZielwortName({
          exercises,
          currentExercise: state.currentExercise,
        });
        audioButtonText = zielwort;
      }
    }

    const payload = {
      exercises,
      settings,
      exerciseClass,
      zielwort,
      audioButtonText,
      ablenkerFromSetJson,
    };
    if (isSelfTraining) {
      payload.currentExercise = 0;
    }
    stateDispatcher(payload);
  };

  const onGoSelect = async () => {
    goHome();
    try {
      await uploadResults();
      await upload();
    } catch (error) {
      logError('', error);
    }
  };

  const onWeiterPress = async (goToNextSlide) => {
    try {
      await uploadResults();
      await upload();
      if (goToNextSlide) {
        nextSlideLogic();
      }
      const data = await getTodaysProgress();
      if (data) {
        stateDispatcher({ currentProgress: data.progress * 0.95 });
      }
    } catch (error) {
      logError('Could not upload on next slide', error);
    }
  };

  const onHomePress = () => {
    onGoSelect();
  };

  useEffect(() => {
    if (isSelfTraining) {
      applyUserId(patientId);
      upload(false, false).then(() => {
        getTodaysProgress().then(function (data) {
          if (data) {
            if (data.showPopup === true) {
              stateDispatcher({ currentProgress: 0 });
            } else {
              stateDispatcher({ currentProgress: data.progress * 0.95 });
            }
          }
        });
      });
    }
    init();
    return () => {
      clearTimeout(timeoutReference);
      timeoutReference = null;
    };
  }, []);

  useEffect(() => {
    if (isSelfTraining) {
      return;
    }
    clearTimeout(timeoutReference);
    const zielwortName = getZielwortName({
      exercises: state.exercises,
      currentExercise: state.currentExercise,
    });
    stateDispatcher({ zielwort: zielwortName });
  }, [state.currentExercise, JSON.stringify(state.exercises)]);

  return {
    activeExercise,
    state,
    skip,
    next,
    PAGER_OPTIONS,
    showZielwort,
    isSolved,
    notSolved,
    maxNumOfExercises,
    // ? modal state
    showResultsModal,
    showResultsModalChange,
    // -----------
    // ? isSelf specific props
    touched,
    stop,
    goSelect,
    checkProgress,
  };
};
